import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SectionHeading } from "@/components/site/Blocks";

export type FaqSectionGroup = {
  id: string;
  title: string;
  lead: string;
  items: Array<[string, string]>;
};

type FaqSectionLabels = {
  navLabel: string;
  sectionEyebrow: string;
  /** Link text pointing at the standalone answer page. */
  detailLabel?: string;
};

/**
 * FAQ sections with a jump navigation and one accordion per group.
 * Keyword search lives in the sitewide search (⌘/Ctrl + K, /search).
 */
export function FaqSections({
  groups,
  labels,
  detailPath,
}: {
  groups: readonly FaqSectionGroup[];
  labels: FaqSectionLabels;
  /** Optional resolver for the standalone answer page of a question. */
  detailPath?: (question: string) => string | undefined;
}) {
  return (
    <>
      <nav aria-label={labels.navLabel} className="section-shell pt-10">
        <ul className="flex flex-wrap gap-2">
          {groups.map((group) => (
            <li key={group.id}>
              <a
                href={`#${group.id}`}
                className="inline-flex rounded-full border border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
              >
                {group.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {groups.map((group, i) => (
        <section
          key={group.id}
          id={group.id}
          className={i % 2 === 1 ? "bg-secondary scroll-mt-24" : "scroll-mt-24"}
        >
          <div className="section-shell py-16">
            <SectionHeading eyebrow={labels.sectionEyebrow} title={group.title} lead={group.lead} />
            <Accordion type="single" collapsible className="mt-8 max-w-3xl">
              {group.items.map(([q, a]) => (
                <AccordionItem key={q} value={q}>
                  <AccordionTrigger className="text-left">{q}</AccordionTrigger>
                  <AccordionContent className="leading-relaxed text-muted-foreground">
                    {a}
                    {detailPath?.(q) && (
                      <a
                        href={detailPath(q)!}
                        className="mt-3 inline-flex text-sm font-bold text-primary hover:underline"
                      >
                        {labels.detailLabel ?? "Read the full answer"}
                      </a>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      ))}
    </>
  );
}
